/**
 * Generic Dashboard Renderer Component
 *
 * @param {string} type - Dashboard type to fetch from backend (e.g. "accounting", "accounting_expenses").
 */

import { Card } from '@material-ui/core';

import './dashboard.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SuccessSnackbar from '../Help/SuccessSnackbar';
import DashboardSkeleton from './Loading';
import { fetchDashboardUrl } from './thunk';

// Refresh iframe every 60 minutes
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

const RenderDashboard = ({ type }) => {
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [dashboardUrlLoading, setDashboardUrlLoading] = useState(true);
  const [dashboardUrlError, setDashboardUrlError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(Date.now()); // Forces iframe re-render

  const errorMessage = useMemo(() => {
    return dashboardUrlError?.error_message || dashboardUrlError?.error || dashboardUrlError;
  }, [dashboardUrlError]);

  // Wrapper function to load/reload dashboard URL
  const loadDashboardUrl = useCallback((t = type) => {
    fetchDashboardUrl({
      type: t,
      setDashboardUrl: url => {
        setDashboardUrl(url);
        setRefreshKey(Date.now()); // forces iframe reload
      },
      setDashboardUrlLoading,
      setDashboardUrlError,
    });
  }, [type]);

  // Load on mount + type change
  useEffect(() => {
    if (type) {
      loadDashboardUrl(type);
    }
  }, [loadDashboardUrl, type]);

  // Auto refresh every REFRESH_INTERVAL_MS(const define above) minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadDashboardUrl();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadDashboardUrl]);

  if (dashboardUrlLoading) {
    return <DashboardSkeleton kpiCount={8}/>;
  }

  if (dashboardUrlError || !!!dashboardUrl) {
    return null;
  }

  return (
    <div className='admin-dashboard-container'>
      <SuccessSnackbar
        open={!!dashboardUrlError}
        error={dashboardUrlError ? errorMessage : null}
        messageText={dashboardUrlError}
        onClose={() => setDashboardUrlError(null)}
      />

      <Card className='admin-dashboard-card' variant='outlined'>
        <iframe
          key={refreshKey}
          title='Admin Dashboard'
          className='admin-dashboard-iframe'
          src={dashboardUrl}
          frameBorder='0'
          width='800'
          height='600'
          allowTransparency
        ></iframe>
      </Card>
    </div>
  );
};

export default RenderDashboard;
